import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invoiceService } from '../services/invoiceService';
import { counterpartyService } from '../services/counterpartyService';
import { companyService } from '../services/companyService';
import { exchangeRateService } from '../services/exchangeRateService';
import { 
  CreateInvoiceRequest, 
  InvoiceStatus, 
  Currency, 
  InvoiceType,
  UnitOfMeasure,
  TaxRate,
  TaxRateLabels,
  TaxRateValues,
  CurrencySymbols,
  PaymentMethod
} from '../types/invoice';
import { Counterparty } from '../types/counterparty';
import { isForeignEuCountry } from '../utils/euCountry';
import { ExchangeRate } from '../types/exchangeRate';
import { useTranslation } from '../hooks/useTranslation';
import { Language, PDF_LANGUAGE_OPTIONS } from '../types/language';
import { useLanguage } from '../contexts/LanguageContext';
import { SearchableSelect } from './SearchableSelect';

interface MultiStepInvoiceModalProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  copyFromInvoice?: any; // Инвойс для копирования
  preselectedClientId?: string; // Предвыбранный клиент
}

interface FormData {
  invoiceNumber: string;
  clientId: string;
  issueDate: string;
  dueDateDays: number;
  saleDate: string;
  placeOfIssue: string;
  invoiceType: InvoiceType;
  exchangeRate: number;
  exchangeRateTable: string;
  tableNumber: string;
  exchangeRateDate: string;
  reverseCharge: boolean;
  bankAccountId: string;
  paymentMethod: PaymentMethod;
  status: InvoiceStatus;
  currency: Currency;
  notes: string;
  internalComment: string;
  pdfLanguage: Language;
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    taxRate: TaxRate;
    unitOfMeasure: UnitOfMeasure;
  }>;
}

type Step = 'basic' | 'items' | 'currency' | 'review';
const MultiStepInvoiceModal: React.FC<MultiStepInvoiceModalProps> = ({ 
    companyId, 
    isOpen, 
    onClose, 
    onSuccess,
    copyFromInvoice,
    preselectedClientId 
  }) => {
    const queryClient = useQueryClient();
    const [currentStep, setCurrentStep] = useState<Step>('basic');
    const [generatedNumber, setGeneratedNumber] = useState<string>('');
    const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
    const [selectedExchangeRate, setSelectedExchangeRate] = useState<ExchangeRate | null>(null);
    const [vatAmountInPln, setVatAmountInPln] = useState<number>(0);
    const [isLoadingRates, setIsLoadingRates] = useState<boolean>(false);
    const { t } = useTranslation();
    const { language: systemLanguage } = useLanguage();
    
    const { register, handleSubmit, reset, control, setValue, watch, getValues, trigger, formState: { errors } } = useForm<FormData>({
      defaultValues: {
        invoiceNumber: '',
        clientId: '',
        issueDate: new Date().toISOString().split('T')[0],
        dueDateDays: 14,
        saleDate: new Date().toISOString().split('T')[0],
        placeOfIssue: '',
        invoiceType: InvoiceType.STANDARD,
        exchangeRate: 0,
        exchangeRateTable: '',
        tableNumber: '',
        exchangeRateDate: '',
        reverseCharge: false,
        bankAccountId: '',
        paymentMethod: PaymentMethod.BANK_TRANSFER,
        status: InvoiceStatus.DRAFT,
        currency: Currency.PLN, // Будет автоматически обновлено при выборе банковского счета
        notes: '',
        internalComment: '',
        pdfLanguage: systemLanguage || Language.PL_EN,
        items: [{ description: '', quantity: 1, unitPrice: 0, taxRate: TaxRate.NP, unitOfMeasure: UnitOfMeasure.PIECE }]
      },
      mode: 'onChange'
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'items'
      });
    
      const watchedValues = watch();
      
      // Добавляем дебаунс для предотвращения частых обновлений
      const [debouncedWatchedValues, setDebouncedWatchedValues] = useState(watchedValues);
      
      useEffect(() => {
        const timeoutId = setTimeout(() => {
          if (isOpen) {
            setDebouncedWatchedValues(watchedValues);
          }
        }, 100);
        
        return () => clearTimeout(timeoutId);
      }, [watchedValues, isOpen]);
    
      const { data: counterparties = [] } = useQuery({
        queryKey: ['counterparties', companyId],
        queryFn: () => counterpartyService.getCompanyCounterparties(companyId)
      });

      const defaultNotSubjectRate = (clientId?: string): TaxRate => {
        const country = counterparties.find((c: Counterparty) => c.id === clientId)?.country;
        return isForeignEuCountry(country) ? TaxRate.NPUE : TaxRate.NP;
      };

      const applyNotSubjectRate = (clientId: string) => {
        const next = defaultNotSubjectRate(clientId);
        const items = getValues('items') || [];
        items.forEach((item, index) => {
          const current = item?.taxRate;
          if (!current || current === TaxRate.NP || current === TaxRate.NPUE) {
            setValue(`items.${index}.taxRate`, next, { shouldValidate: true, shouldDirty: true });
          }
        });
      };
    
      const { data: company } = useQuery({
        queryKey: ['company', companyId],
        queryFn: () => companyService.getCompany(companyId)
      });
    
      // Показываем предварительный номер инвойса при открытии модального окна (без инкремента)
      useEffect(() => {
        if (isOpen && !generatedNumber) {
          invoiceService.previewInvoiceNumber(companyId).then(response => {
            setGeneratedNumber(response.invoiceNumber);
            setValue('invoiceNumber', response.invoiceNumber);
          });
        }
      }, [isOpen, companyId, generatedNumber, setValue]);
    
      // Автоматически заполняем место выставления городом компании
      useEffect(() => {
        if (company && company.city && isOpen) {
          setValue('placeOfIssue', company.city);
        }
      }, [company, isOpen, setValue]);
      
      // Заполняем форму данными из копируемого инвойса
      useEffect(() => {
        if (copyFromInvoice && isOpen) {
          setValue('clientId', copyFromInvoice.clientId || '');
          // Валюта будет установлена автоматически при выборе банковского счета
          setValue('bankAccountId', copyFromInvoice.bankAccountId || '');
          setValue('invoiceType', copyFromInvoice.invoiceType || InvoiceType.STANDARD);
          setValue('notes', copyFromInvoice.notes || '');
          setValue('internalComment', copyFromInvoice.internalComment || '');
          setValue('pdfLanguage', (copyFromInvoice.pdfLanguage as Language) || systemLanguage || Language.PL_ONLY);
          
          // Копируем позиции инвойса
          if (copyFromInvoice.items && copyFromInvoice.items.length > 0) {
            setValue('items', copyFromInvoice.items.map((item: any) => ({
              description: item.description || '',
              quantity: item.quantity || 1,
              unitPrice: item.unitPrice || 0,
              // Убеждаемся, что taxRate правильно установлен
              taxRate: (item.taxRate && Object.values(TaxRate).includes(item.taxRate as TaxRate)) 
                ? item.taxRate 
                : TaxRate.NP,
              unitOfMeasure: item.unitOfMeasure || UnitOfMeasure.PIECE
            })));
          }
          
          // Даты выставления и продажи устанавливаются автоматически на текущие
          const today = new Date().toISOString().split('T')[0];
          setValue('issueDate', today);
          setValue('saleDate', today);
        }
      }, [copyFromInvoice, isOpen, setValue, systemLanguage]);
      
      // Устанавливаем язык по умолчанию при открытии формы (только если не копируем)
      useEffect(() => {
        if (isOpen && !copyFromInvoice) {
          // По умолчанию используем польский + английский
          setValue('pdfLanguage', Language.PL_EN);
        }
      }, [isOpen, copyFromInvoice, setValue]);
      
      // Убеждаемся, что все позиции имеют правильное значение taxRate
      useEffect(() => {
        if (isOpen && fields.length > 0) {
          fields.forEach((field, index) => {
            const currentValue = getValues(`items.${index}.taxRate`);
            if (!currentValue || !Object.values(TaxRate).includes(currentValue as TaxRate)) {
              const next = defaultNotSubjectRate(getValues('clientId'));
              setValue(`items.${index}.taxRate`, next, { shouldValidate: true, shouldDirty: true });
            }
          });
        }
      }, [isOpen, fields, getValues, setValue]);
    
      // Предзаполняем клиента, если передан preselectedClientId
      useEffect(() => {
        if (preselectedClientId && isOpen && !copyFromInvoice) {
          setValue('clientId', preselectedClientId);
        }
      }, [preselectedClientId, isOpen, copyFromInvoice, setValue]);

      // Когда контрагент из ЕС (кроме Польши), ставка по умолчанию — nie podl. UE.
      useEffect(() => {
        if (!isOpen || copyFromInvoice || counterparties.length === 0) {
          return;
        }
        const clientId = getValues('clientId');
        if (clientId) {
          applyNotSubjectRate(clientId);
        }
      }, [isOpen, copyFromInvoice, counterparties, watchedValues.clientId]);
    
      // Функция для поиска предыдущего рабочего дня (исключая субботу и воскресенье)
      const getPreviousWorkingDay = (date: Date): Date => {
        const previousDay = new Date(date);
        previousDay.setDate(previousDay.getDate() - 1);
        
        // Пропускаем выходные дни (суббота = 6, воскресенье = 0)
        while (previousDay.getDay() === 6 || previousDay.getDay() === 0) {
          previousDay.setDate(previousDay.getDate() - 1);
        }
        
        return previousDay;
      };

      // Загружаем курсы валют для третьего шага
      useEffect(() => {
        let isCancelled = false;
        
        const loadRatesAsync = async () => {
          if (isOpen && currentStep === 'currency' && debouncedWatchedValues.currency !== Currency.PLN) {
            setIsLoadingRates(true);
            try {
              // ВСЕГДА используем saleDate как приоритетную дату, только если она не указана - используем issueDate
              const baseDate = (debouncedWatchedValues.saleDate && debouncedWatchedValues.saleDate.trim() !== '') 
                ? debouncedWatchedValues.saleDate 
                : debouncedWatchedValues.issueDate;
              const baseDateObj = new Date(baseDate);
              
              // Загружаем курсы за период (30 дней назад от базовой даты до базовой даты)
              const rates = await exchangeRateService.getExchangeRatesForPeriod(
                debouncedWatchedValues.currency,
                new Date(baseDateObj.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                baseDate
              );
              
              if (!isCancelled) {
                setExchangeRates(rates);
                setIsLoadingRates(false);
                
                // Находим курс предыдущего рабочего дня от даты продажи
                const findPreviousWorkingDayRate = (saleDate: string, exchangeRates: ExchangeRate[]) => {
                  const saleDateObj = new Date(saleDate);
                  const previousWorkingDay = getPreviousWorkingDay(saleDateObj);
                  
                  // Сортируем курсы по дате (от новых к старым)
                  const sortedRates = exchangeRates.sort((a, b) => 
                    new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
                  );
                  
                  // Ищем курс на предыдущий рабочий день или ближайший доступный курс до этой даты
                  for (const rate of sortedRates) {
                    const rateDate = new Date(rate.effectiveDate);
                    // Ищем курс, который не позже предыдущего рабочего дня
                    if (rateDate <= previousWorkingDay) {
                      return rate;
                    }
                  }
                  
                  // Если не нашли, возвращаем самый старый доступный курс
                  return sortedRates[sortedRates.length - 1] || null;
                };
                
                // Автоматически выбираем курс предыдущего рабочего дня от даты продажи
                // Всегда переопределяем курс при изменении базовой даты (saleDate или issueDate)
                const preferredRate = findPreviousWorkingDayRate(baseDate, rates);
                if (preferredRate && !isCancelled) {
                  setSelectedExchangeRate(preferredRate);
                  setValue('exchangeRate', preferredRate.rate);
                  setValue('exchangeRateTable', preferredRate.tableType);
                  setValue('exchangeRateDate', preferredRate.effectiveDate);
                  console.log('Автоматически выбран курс валют на предыдущий рабочий день от даты продажи:', preferredRate);
                }
              }
            } catch (error) {
              if (!isCancelled) {
                console.error('Ошибка загрузки курсов валют:', error);
                setIsLoadingRates(false);
                setExchangeRates([]);
              }
            }
          } else {
            setIsLoadingRates(false);
          }
        };
        
        loadRatesAsync();
        
        return () => {
          isCancelled = true;
        };
      }, [isOpen, currentStep, debouncedWatchedValues.currency, debouncedWatchedValues.saleDate, debouncedWatchedValues.issueDate, setValue]);
    
    
      const calculateVatInPln = useCallback((exchangeRate?: ExchangeRate | null) => {
        const rateToUse = exchangeRate || selectedExchangeRate;
        if (!rateToUse || watchedValues.currency === Currency.PLN) {
          return 0;
        }
    
        const totalAmount = watchedValues.items.reduce((sum, item) => {
          const itemTotal = item.quantity * item.unitPrice;
          const taxAmount = itemTotal * TaxRateValues[item.taxRate];
          return sum + taxAmount;
        }, 0);
    
        return totalAmount * rateToUse.rate;
      }, [selectedExchangeRate, watchedValues.currency, watchedValues.items]);
    

      useEffect(() => {
        if (isOpen && currentStep === 'currency' && selectedExchangeRate) {
          const vatAmount = calculateVatInPln();
          setVatAmountInPln(vatAmount);
        } else if (isOpen && currentStep === 'currency' && !selectedExchangeRate) {
          setVatAmountInPln(0);
        }
      }, [isOpen, currentStep, selectedExchangeRate, debouncedWatchedValues.items, calculateVatInPln]);

      // Автоматически устанавливаем валюту из выбранного банковского счета
      useEffect(() => {
        if (isOpen && debouncedWatchedValues.bankAccountId && company?.bankAccounts) {
          const selectedAccount = company.bankAccounts.find(account => account.id === debouncedWatchedValues.bankAccountId);
          if (selectedAccount) {
            setValue('currency', selectedAccount.currency as Currency);
          }
        }
      }, [isOpen, debouncedWatchedValues.bankAccountId, company?.bankAccounts, setValue]);
    
      const createInvoiceMutation = useMutation({
        mutationFn: (data: CreateInvoiceRequest) => invoiceService.createInvoice(companyId, data),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['invoices', companyId] });
          handleClose();
          onSuccess();
        },
        onError: (error) => {
          console.error('Ошибка создания инвойса:', error);
        }
      });
    
      const handleClose = () => {
        // Используем requestAnimationFrame для предотвращения ошибок ResizeObserver
        requestAnimationFrame(() => {
          // Сначала вызываем onClose, чтобы isOpen стал false и остановил useEffect
          onClose();
          
          // Затем очищаем состояние с небольшой задержкой, чтобы избежать race conditions
          setTimeout(() => {
            reset();
            setCurrentStep('basic');
            setGeneratedNumber('');
            setExchangeRates([]);
            setSelectedExchangeRate(null);
            setVatAmountInPln(0);
          }, 0);
        });
      };
    
      const nextStep = async () => {
        switch (currentStep) {
          case 'basic':
            // Триггерим валидацию для обязательных полей перед переходом на следующий шаг
            const isValid = await trigger(['clientId', 'bankAccountId']);
            if (!isValid) {
              // Валидация не прошла - поля будут подсвечены красным, ошибки отобразятся автоматически
              return;
            }
            setCurrentStep('items');
            break;
          case 'items':
            // Валидируем все позиции перед переходом на следующий шаг
            const itemFieldsToValidate = watchedValues.items.flatMap((_, index) => [
              `items.${index}.description` as const,
              `items.${index}.unitPrice` as const
            ]);
            const itemsValid = await trigger(itemFieldsToValidate);
            
            if (!itemsValid) {
              // Валидация не прошла - поля будут подсвечены красным, ошибки отобразятся автоматически
              return;
            }
            
            // Проверяем нужен ли шаг с курсом валют
            const hasNonZeroVat = watchedValues.items.some(item => 
              TaxRateValues[item.taxRate] > 0
            );
            // Если валюта не PLN и есть НДС, переходим на шаг выбора курса валют
            if (watchedValues.currency !== Currency.PLN && hasNonZeroVat) {
              setCurrentStep('currency');
            } else {
              // Если валюта PLN или нет НДС, переходим на финальный шаг
              setCurrentStep('review');
            }
            break;
          case 'currency':
            // Проверяем, что курс валют выбран перед переходом на review
            if (!selectedExchangeRate && watchedValues.currency !== Currency.PLN) {
              alert(t('invoices.exchangeRates.selectRateRequired') || 'Пожалуйста, выберите курс валют');
              return;
            }
            setCurrentStep('review');
            break;
        }
      };
      const prevStep = () => {
        switch (currentStep) {
          case 'items':
            setCurrentStep('basic');
            break;
          case 'currency':
            setCurrentStep('items');
            break;
          case 'review':
            const hasNonZeroVat = watchedValues.items.some(item => 
              TaxRateValues[item.taxRate] > 0
            );
            if (watchedValues.currency !== Currency.PLN && hasNonZeroVat) {
              setCurrentStep('currency');
            } else {
              setCurrentStep('items');
            }
            break;
        }
      };
    
      const onSubmit = (data: FormData) => {
        // Получаем актуальные значения из формы, включая значения из Controller
        const currentItems = getValues('items');
        
        console.log('onSubmit - data.items:', data.items);
        console.log('onSubmit - currentItems (getValues):', currentItems);
        console.log('onSubmit - watchedValues.items:', watchedValues.items);
        
        // Функция для конвертации даты в строку формата YYYY-MM-DD
        const formatDateToString = (dateValue: string | number[] | undefined): string | undefined => {
          if (!dateValue) return undefined;
          
          // Если это массив [год, месяц, день]
          if (Array.isArray(dateValue) && dateValue.length >= 3) {
            const [year, month, day] = dateValue;
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
          
          // Если это строка, проверяем формат
          if (typeof dateValue === 'string') {
            // Если уже в формате YYYY-MM-DD, возвращаем как есть
            if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return dateValue;
            }
            // Иначе пытаемся распарсить и отформатировать
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          }
          
          return undefined;
        };
        
        const today = new Date().toISOString().split('T')[0];
        const requestData: CreateInvoiceRequest = {
          ...data,
          issueDate: today,
          dueDate: data.dueDateDays ? 
            new Date(new Date(today).getTime() + data.dueDateDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : 
            undefined,
          exchangeRate: selectedExchangeRate?.rate || data.exchangeRate,
          exchangeRateTable: selectedExchangeRate?.tableType || data.exchangeRateTable,
          tableNumber: selectedExchangeRate?.tableNumber || data.tableNumber,
          exchangeRateDate: formatDateToString(selectedExchangeRate?.effectiveDate || data.exchangeRateDate),
          notes: data.notes || undefined,
          internalComment: data.internalComment || undefined,
          paymentMethod: data.paymentMethod || PaymentMethod.BANK_TRANSFER,
          pdfLanguage: data.pdfLanguage,
          items: currentItems.map((item, index) => {
            // Используем значение из getValues, если оно есть, иначе из data, иначе из watchedValues
            let taxRateValue = item.taxRate || data.items[index]?.taxRate || watchedValues.items[index]?.taxRate;
            
            console.log(`Item ${index} taxRate:`, {
              fromGetValues: item.taxRate,
              fromData: data.items[index]?.taxRate,
              fromWatched: watchedValues.items[index]?.taxRate,
              final: taxRateValue
            });
            
            // Убеждаемся, что taxRate установлен правильно - если undefined, null, невалидный или пустой, используем NP
            if (!taxRateValue || 
                typeof taxRateValue !== 'string' || 
                !Object.values(TaxRate).includes(taxRateValue as TaxRate)) {
              console.warn(`Item ${index} has invalid taxRate: ${taxRateValue}, using NP`);
              taxRateValue = defaultNotSubjectRate(data.clientId);
            }
            
            return {
              description: item.description || data.items[index]?.description || '',
              quantity: item.quantity || data.items[index]?.quantity || 1,
              unitPrice: item.unitPrice || data.items[index]?.unitPrice || 0,
              taxRate: taxRateValue,
              unitOfMeasure: item.unitOfMeasure || data.items[index]?.unitOfMeasure || UnitOfMeasure.PIECE
            };
          })
        };
        
        console.log('onSubmit - final requestData.items:', requestData.items);
    
        createInvoiceMutation.mutate(requestData);
      };
    
      const addItem = () => {
        const newItem = { 
          description: '', 
          quantity: 1, 
          unitPrice: 0, 
          taxRate: defaultNotSubjectRate(getValues('clientId')), 
          unitOfMeasure: UnitOfMeasure.PIECE 
        };
        append(newItem);
        // Убеждаемся, что значение установлено после добавления
        setTimeout(() => {
          const lastIndex = fields.length;
          setValue(`items.${lastIndex}.taxRate`, defaultNotSubjectRate(getValues('clientId')), { shouldValidate: true, shouldDirty: true });
        }, 0);
      };
    
      const removeItem = (index: number) => {
        if (fields.length > 1) {
          remove(index);
        }
      };
    
      if (!isOpen) return null;
    
      return (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            // Закрываем только при клике на фон, не на содержимое
            if (e.target === e.currentTarget) {
              handleClose();
            }
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('invoices.createInvoice')}</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
    
            {/* Индикатор шагов */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-center">
                {[
              { key: 'basic', label: t('invoices.multiStep.basicInfo') },
              { key: 'items', label: t('invoices.multiStep.items') },
              { key: 'currency', label: t('invoices.multiStep.currency') },
              { key: 'review', label: t('invoices.multiStep.review') }
                ].map((step, index) => {
                  const stepKey = step.key as Step;
                  const isActive = currentStep === stepKey;
                  const isCompleted = ['basic', 'items', 'currency', 'review'].indexOf(currentStep) > index;
                  
                  return (
                    <div key={step.key} className="flex items-center">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        isActive 
                          ? 'bg-blue-600 text-white' 
                          : isCompleted 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <span className={`ml-2 text-sm ${
                        isActive ? 'text-blue-600 font-medium' : 'text-gray-500'
                      }`}>
                        {step.label}
                      </span>
                      {index < 3 && (
                        <div className="w-8 h-px bg-gray-300 mx-2"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
    
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              {/* Шаг 1: Основная информация */}
              {currentStep === 'basic' && (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-900">{t('invoices.multiStep.basicInfo')}</h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.invoiceNumber')} *
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            {...register('invoiceNumber', { required: t('invoices.validation.invoiceNumberRequired') })}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="INV-001"
          />
          <button
            type="button"
            onClick={() => {
              invoiceService.previewInvoiceNumber(companyId).then(response => {
                setGeneratedNumber(response.invoiceNumber);
                setValue('invoiceNumber', response.invoiceNumber);
              });
            }}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title={t('invoiceNumbering.generateNewTooltip')}
          >
            🔄
          </button>
        </div>
        {errors.invoiceNumber && (
          <p className="mt-1 text-sm text-red-600">{errors.invoiceNumber.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.clientId')} *
        </label>
        <Controller
          name="clientId"
          control={control}
          rules={{ required: t('invoices.validation.clientRequired') || 'Клиент обязателен для заполнения' }}
          render={({ field, fieldState }) => (
            <>
              <SearchableSelect
                options={counterparties.map((counterparty: Counterparty) => ({
                  value: counterparty.id,
                  label: counterparty.name,
                  searchText: `${counterparty.name} ${counterparty.taxId || ''} ${counterparty.vatId || ''} ${counterparty.email || ''} ${counterparty.phone || ''} ${counterparty.comment || ''}`.trim()
                }))}
                value={field.value || ''}
                onChange={(value) => {
                  field.onChange(value);
                  if (value) {
                    trigger('clientId');
                    applyNotSubjectRate(value);
                  }
                }}
                placeholder={t('common.selectClient')}
                required
                error={fieldState.error?.message}
              />
            </>
          )}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.invoiceType')} *
        </label>
        <select
          {...register('invoiceType', { required: t('invoices.validation.invoiceTypeRequired') })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {Object.values(InvoiceType).map(type => (
            <option key={type} value={type}>
              {t(`invoices.types.${type}`)}
            </option>
          ))}
        </select>
        {errors.invoiceType && (
          <p className="mt-1 text-sm text-red-600">{errors.invoiceType.message}</p>
        )}
      </div>

      {/* Валюта теперь берется автоматически из выбранного банковского счета и отображается только на последнем шаге */}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.dueDate')} (дни)
        </label>
        <input
          type="number"
          min="1"
          {...register('dueDateDays', { 
            required: t('invoices.validation.dueDaysRequired'),
            min: { value: 1, message: t('invoices.validation.dueDaysMin') }
          })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="14"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.saleDate')}
        </label>
        <input
          type="date"
          {...register('saleDate')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.placeOfIssue')} *
        </label>
        <input
          type="text"
          {...register('placeOfIssue', { required: t('invoices.validation.placeOfIssueRequired') })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.placeOfIssue ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Warszawa"
        />
        {errors.placeOfIssue && (
          <p className="mt-1 text-sm text-red-600">{errors.placeOfIssue.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('companies.bankAccount')} *
        </label>
        <Controller
          name="bankAccountId"
          control={control}
          rules={{ required: t('invoices.validation.bankAccountRequired') || 'Банковский счет обязателен для заполнения' }}
          render={({ field, fieldState }) => (
            <>
              <SearchableSelect
                options={company?.bankAccounts?.map((account) => ({
                  value: account.id,
                  label: `${account.name} - ${account.accountNumber} (${account.currency})`,
                  searchText: `${account.name} ${account.accountNumber} ${account.bankName} ${account.currency}`.trim()
                })) || []}
                value={field.value || ''}
                onChange={(value) => {
                  field.onChange(value);
                  if (value) {
                    trigger('bankAccountId');
                  }
                  // Автоматически устанавливаем валюту из выбранного счета
                  const selectedAccount = company?.bankAccounts?.find(acc => acc.id === value);
                  if (selectedAccount) {
                    setValue('currency', selectedAccount.currency);
                  }
                }}
                placeholder={t('common.selectBankAccount')}
                required
                error={fieldState.error?.message}
              />
            </>
          )}
        />
        {watch('bankAccountId') && (() => {
          const selectedAccount = company?.bankAccounts?.find(acc => acc.id === watch('bankAccountId'));
          if (selectedAccount?.bankAddress) {
            return (
              <div className="mt-2 p-2 bg-gray-50 rounded-md">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('companies.bankAddress')}
                </label>
                <p className="text-sm text-gray-700">{selectedAccount.bankAddress}</p>
              </div>
            );
          }
          return null;
        })()}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.paymentMethod') || 'Способ оплаты'} *
        </label>
        <select
          {...register('paymentMethod', { required: true })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={PaymentMethod.BANK_TRANSFER}>
            {t('invoices.paymentMethods.BANK_TRANSFER') || 'Банковский перевод'}
          </option>
          <option value={PaymentMethod.CASH}>
            {t('invoices.paymentMethods.CASH') || 'Наличные'}
          </option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.pdfLanguage') || 'Język faktury'} *
        </label>
        <select
          {...register('pdfLanguage', { required: true })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {PDF_LANGUAGE_OPTIONS.map(option => (
            <option key={option.code} value={option.code}>
              {option.nativeName}
            </option>
          ))}
        </select>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.internalComment') || 'Комментарий'}
        </label>
        <textarea
          {...register('internalComment')}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('invoices.internalCommentPlaceholder') || 'Внутренний комментарий (не отображается в фактуре)...'}
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('invoices.internalCommentHint') || 'Этот комментарий виден только вам и не будет отображаться в PDF фактуре'}
        </p>
      </div>
    </div>
  </div>
)}

{/* Шаг 2: Позиции инвойса */}
{currentStep === 'items' && (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-900">{t('invoices.multiStep.items')}</h3>
    
    {/* Таблица позиций */}
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700 w-2/5">
              {t('invoices.description')} *
            </th>
            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700 w-20">
              {t('invoices.quantity')} *
            </th>
            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700 w-24">
              {t('invoices.unitOfMeasure')}
            </th>
            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700 w-28">
              {t('invoices.unitPrice')} *
            </th>
            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700 w-32">
              {t('invoices.taxRate')}
            </th>
            <th className="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700 w-16">
              {t('common.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => (
            <tr key={field.id}>
              <td className="border border-gray-200 px-2 py-1">
                <input
                  type="text"
                  {...register(`items.${index}.description`, { 
                    required: t('invoices.validation.descriptionRequired') || 'Описание обязательно',
                    validate: (value) => value?.trim() !== '' || (t('invoices.validation.descriptionRequired') || 'Описание обязательно')
                  })}
                  className={`w-full px-2 py-1 border rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm ${
                    errors.items?.[index]?.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('invoices.descriptionPlaceholder')}
                />
                {errors.items?.[index]?.description && (
                  <p className="mt-1 text-xs text-red-600">{errors.items[index]?.description?.message}</p>
                )}
              </td>
              <td className="border border-gray-200 px-2 py-1">
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  {...register(`items.${index}.quantity`, { 
                    required: t('invoices.validation.quantityRequired'),
                    min: { value: 0.01, message: t('invoices.validation.quantityMin') }
                  })}
                  className={`w-full px-2 py-1 border rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm ${
                    errors.items?.[index]?.quantity ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.items?.[index]?.quantity && (
                  <p className="mt-1 text-xs text-red-600">{errors.items[index]?.quantity?.message}</p>
                )}
              </td>
              <td className="border border-gray-200 px-2 py-1">
                <select
                  {...register(`items.${index}.unitOfMeasure`)}
                  className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  {Object.values(UnitOfMeasure).map(unit => (
                    <option key={unit} value={unit}>
                      {t(`invoices.unitOfMeasures.${unit}`)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="border border-gray-200 px-2 py-1">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register(`items.${index}.unitPrice`, { 
                    required: t('invoices.validation.unitPriceRequired'),
                    min: { value: 0.01, message: t('invoices.validation.unitPriceMin') || 'Цена должна быть больше 0' },
                    validate: (value) => value > 0 || (t('invoices.validation.unitPriceMin') || 'Цена должна быть больше 0')
                  })}
                  className={`w-full px-2 py-1 border rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm ${
                    errors.items?.[index]?.unitPrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.items?.[index]?.unitPrice && (
                  <p className="mt-1 text-xs text-red-600">{errors.items[index]?.unitPrice?.message}</p>
                )}
              </td>
              <td className="border border-gray-200 px-2 py-1">
                <Controller
                  name={`items.${index}.taxRate`}
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => {
                    // Определяем, является ли фактура фактурой без VAT
                    const invoiceType = watchedValues.invoiceType;
                    const hasVatId = company?.vatId != null && company.vatId.trim() !== '';
                    const isNoVatInvoice = !hasVatId || 
                                          invoiceType === InvoiceType.STANDARD_NO_VAT || 
                                          invoiceType === InvoiceType.PROFORMA_NO_VAT;
                    
                    // Для фактур без VAT автоматически устанавливаем ZW
                    const fallbackRate = defaultNotSubjectRate(watchedValues.clientId);
                    const currentValue = isNoVatInvoice ? TaxRate.ZW : (field.value || fallbackRate);
                    if (isNoVatInvoice && field.value !== TaxRate.ZW) {
                      setTimeout(() => field.onChange(TaxRate.ZW), 0);
                    } else if (!Object.values(TaxRate).includes(currentValue as TaxRate)) {
                      setTimeout(() => field.onChange(fallbackRate), 0);
                    }
                    
                    return (
                      <select
                        {...field}
                        value={currentValue}
                        onChange={(e) => {
                          if (isNoVatInvoice) {
                            // Для фактур без VAT всегда ZW
                            field.onChange(TaxRate.ZW);
                          } else {
                            const newValue = e.target.value as TaxRate;
                            console.log(`taxRate changed for item ${index}:`, newValue);
                            field.onChange(newValue);
                          }
                        }}
                        disabled={isNoVatInvoice}
                        className={`w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm ${
                          isNoVatInvoice ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      >
                        {Object.values(TaxRate).map(rate => (
                          <option key={rate} value={rate}>
                            {TaxRateLabels[rate]}
                          </option>
                        ))}
                      </select>
                    );
                  }}
                />
              </td>
              <td className="border border-gray-200 px-2 py-1 text-center">
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    title={t('common.delete')}
                  >
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="flex justify-between items-center">
      <button
        type="button"
        onClick={addItem}
        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
      >
        + {t('invoices.multiStep.addItem')}
      </button>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t('invoices.notes')}
      </label>
      <textarea
        {...register('notes')}
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder={t('invoices.notesPlaceholder')}
      />
    </div>

    {/* Информация о валюте и необходимости выбора курса */}
    {watchedValues.currency && watchedValues.currency !== Currency.PLN && (
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>{t('invoices.currency')}:</strong> {watchedValues.currency}
        </p>
        <p className="text-xs text-blue-600 mt-1">
          {t('invoices.exchangeRates.willBeRequired') || 'На следующем шаге необходимо будет выбрать курс валют для расчета НДС в PLN.'}
        </p>
      </div>
    )}
    
    {/* Общее сообщение об ошибке валидации для позиций */}
    {watchedValues.items.some((_, index) => 
      errors.items?.[index]?.description || errors.items?.[index]?.unitPrice || errors.items?.[index]?.quantity
    ) && (
      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-600">
          {t('invoices.validation.fillRequiredFields') || 'Пожалуйста, заполните все обязательные поля'}
        </p>
      </div>
    )}
  </div>
)}

{/* Шаг 3: Курс валют */}
{currentStep === 'currency' && (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-900">{t('invoices.exchangeRates.title')}</h3>
    
    <div className="bg-blue-50 p-4 rounded-lg">
      <p className="text-sm text-blue-800">
        {t('invoices.exchangeRates.description')} {watchedValues.currency} {t('invoices.exchangeRates.forDate')} ({new Date((watchedValues.saleDate && watchedValues.saleDate.trim() !== '') ? watchedValues.saleDate : watchedValues.issueDate).toLocaleDateString()}).
      </p>
      <p className="text-xs text-blue-600 mt-1">
        {t('invoices.exchangeRates.pleaseSelect') || 'Пожалуйста, выберите курс валют из списка ниже.'}
      </p>
    </div>

    {isLoadingRates ? (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">{t('invoices.exchangeRates.loading') || 'Загрузка курсов валют...'}</span>
      </div>
    ) : exchangeRates.length === 0 ? (
      <div className="bg-yellow-50 p-4 rounded-lg space-y-4">
        <p className="text-sm text-yellow-800">
          {t('invoices.exchangeRates.noRates') || 'Курсы валют не найдены для выбранной даты. Пожалуйста, добавьте курс валют вручную.'}
        </p>
        <button
          type="button"
          onClick={async () => {
            setIsLoadingRates(true);
            try {
              // Загружаем курсы для даты, предшествующей дате продажи (saleDate) или дате выставления (issueDate)
              const baseDate = (watchedValues.saleDate && watchedValues.saleDate.trim() !== '') 
                ? watchedValues.saleDate 
                : watchedValues.issueDate;
              const baseDateObj = new Date(baseDate);
              
              // Пытаемся загрузить курсы для нескольких дат (начиная с предыдущей, до 7 дней назад)
              const datesToTry = [];
              for (let i = 1; i <= 7; i++) {
                const date = new Date(baseDateObj.getTime() - i * 24 * 60 * 60 * 1000);
                datesToTry.push(date.toISOString().split('T')[0]);
              }
              
              let loadedRates: ExchangeRate[] = [];
              for (const date of datesToTry) {
                try {
                  // Обновляем курсы для этой даты
                  const allRates = await exchangeRateService.updateExchangeRatesForDate(date);
                  if (allRates && allRates.length > 0) {
                    // Фильтруем по нужной валюте
                    const currencyRates = allRates.filter(rate => {
                      const rateCurrency = (rate.currencyCode || '').toUpperCase();
                      return rateCurrency === watchedValues.currency.toUpperCase();
                    });
                    if (currencyRates.length > 0) {
                      loadedRates = currencyRates;
                      break;
                    }
                  }
                } catch (error) {
                  console.log(`No rates for date ${date}:`, error);
                }
              }
              
              if (loadedRates.length > 0) {
                setExchangeRates(loadedRates);
                // Автоматически выбираем первый курс (самый близкий к дате продажи)
                const firstRate = loadedRates[0];
                setSelectedExchangeRate(firstRate);
                setValue('exchangeRate', firstRate.rate);
                setValue('exchangeRateTable', firstRate.tableType);
                setValue('exchangeRateDate', firstRate.effectiveDate);
              } else {
                // Если не удалось загрузить для конкретных дат, пробуем обновить все курсы
                await exchangeRateService.updateExchangeRates();
                // Перезагружаем курсы для периода от даты продажи (saleDate) или даты выставления (issueDate)
                const baseDate = (watchedValues.saleDate && watchedValues.saleDate.trim() !== '') 
                  ? watchedValues.saleDate 
                  : watchedValues.issueDate;
                const baseDateObj = new Date(baseDate);
                const rates = await exchangeRateService.getExchangeRatesForPeriod(
                  watchedValues.currency,
                  new Date(baseDateObj.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                  baseDate
                );
                const filteredRates = rates.filter(rate => {
                  const rateCurrency = (rate.currencyCode || '').toUpperCase();
                  return rateCurrency === watchedValues.currency.toUpperCase();
                });
                setExchangeRates(filteredRates);
                if (filteredRates.length > 0) {
                  const firstRate = filteredRates[0];
                  setSelectedExchangeRate(firstRate);
                  setValue('exchangeRate', firstRate.rate);
                  setValue('exchangeRateTable', firstRate.tableType);
                  setValue('exchangeRateDate', firstRate.effectiveDate);
                }
              }
            } catch (error) {
              console.error('Ошибка загрузки курсов валют:', error);
              alert(t('invoices.exchangeRates.loadError') || 'Не удалось загрузить курсы валют. Пожалуйста, попробуйте позже.');
            } finally {
              setIsLoadingRates(false);
            }
          }}
          disabled={isLoadingRates}
          className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoadingRates ? t('invoices.exchangeRates.loading') || 'Загрузка...' : t('invoices.exchangeRates.loadRates') || 'Загрузить актуальные курсы валют'}
        </button>
      </div>
    ) : (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('invoices.exchangeRates.selectRate')}
        </label>
      <select
        value={selectedExchangeRate?.id || ''}
        onChange={(e) => {
          const rate = exchangeRates.find(r => r.id === e.target.value);
          if (rate) {
            setSelectedExchangeRate(rate);
            setValue('exchangeRate', rate.rate);
            setValue('exchangeRateTable', rate.tableType);
            setValue('exchangeRateDate', rate.effectiveDate);
            // Пересчитываем НДС в PLN после выбора курса
            const vatAmount = calculateVatInPln();
            setVatAmountInPln(vatAmount);
          } else {
            // Если выбрано пустое значение, сбрасываем курс
            setSelectedExchangeRate(null);
            setValue('exchangeRate', 0);
            setValue('exchangeRateTable', '');
            setValue('exchangeRateDate', '');
            setVatAmountInPln(0);
          }
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        required
      >
        <option value="">{t('invoices.exchangeRates.selectRate')} *</option>
        {exchangeRates.map((rate) => (
          <option key={rate.id} value={rate.id}>
            {new Date(rate.effectiveDate).toLocaleDateString()} - {rate.rate.toFixed(4)} PLN ({t('invoices.exchangeRates.table')} {rate.tableType})
          </option>
        ))}
      </select>
      {!selectedExchangeRate && (
        <p className="mt-1 text-sm text-red-600">
          {t('invoices.exchangeRates.selectRateRequired') || 'Пожалуйста, выберите курс валют'}
        </p>
      )}
      </div>
    )}

    {selectedExchangeRate && (
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">{t('invoices.exchangeRates.rateInfo')}:</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">{t('invoices.exchangeRates.date')}:</span>
            <span className="ml-2 font-medium">{new Date(selectedExchangeRate.effectiveDate).toLocaleDateString()}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('invoices.exchangeRates.rate')}:</span>
            <span className="ml-2 font-medium">{selectedExchangeRate.rate.toFixed(4)} PLN</span>
          </div>
          <div>
            <span className="text-gray-600">{t('invoices.exchangeRates.table')}:</span>
            <span className="ml-2 font-medium">{selectedExchangeRate.tableType}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('invoices.exchangeRates.vatInPln')}:</span>
            <span className="ml-2 font-medium text-green-600">{vatAmountInPln.toFixed(2)} zł</span>
          </div>
        </div>
      </div>
    )}
  </div>
)}

{/* Шаг 4: Ревью */}
{currentStep === 'review' && (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-900">{t('invoices.reviewStep.title')}</h3>
    
    <div className="bg-gray-50 p-4 rounded-lg space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-sm text-gray-600">{t('invoices.reviewStep.invoiceNumber')}:</span>
          <p className="font-medium">{watchedValues.invoiceNumber}</p>
        </div>
        <div>
          <span className="text-sm text-gray-600">{t('invoices.reviewStep.client')}:</span>
          <p className="font-medium">
            {counterparties.find(c => c.id === watchedValues.clientId)?.name || t('invoices.reviewStep.notSelected')}
          </p>
        </div>
        <div>
          <span className="text-sm text-gray-600">{t('invoices.reviewStep.invoiceType')}:</span>
          <p className="font-medium">{t(`invoices.types.${watchedValues.invoiceType}`)}</p>
        </div>
        <div>
          <span className="text-sm text-gray-600">{t('invoices.reviewStep.currency')}:</span>
          <p className="font-medium">{watchedValues.currency} {CurrencySymbols[watchedValues.currency]}</p>
        </div>
        <div>
          <span className="text-sm text-gray-600">{t('invoices.reviewStep.issueDate')}:</span>
          <p className="font-medium">{new Date(watchedValues.issueDate).toLocaleDateString('ru-RU')}</p>
        </div>
        <div>
          <span className="text-sm text-gray-600">{t('invoices.reviewStep.dueDays')}:</span>
          <p className="font-medium">{watchedValues.dueDateDays} {t('invoices.reviewStep.days')}</p>
        </div>
      </div>
      
      {selectedExchangeRate && (
        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-2">{t('invoices.reviewStep.exchangeRateInfo')}:</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600">{t('invoices.reviewStep.rateDate')}:</span>
              <p className="font-medium">{new Date(selectedExchangeRate.effectiveDate).toLocaleDateString('ru-RU')}</p>
            </div>
            <div>
              <span className="text-gray-600">{t('invoices.exchangeRates.rate')}:</span>
              <p className="font-medium">{selectedExchangeRate.rate.toFixed(4)} PLN</p>
            </div>
            <div>
              <span className="text-gray-600">{t('invoices.exchangeRates.vatInPln')}:</span>
              <p className="font-medium text-green-600">{vatAmountInPln.toFixed(2)} zł</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="border-t pt-4">
        <h4 className="font-medium text-gray-900 mb-2">{t('invoices.reviewStep.positions')}:</h4>
        <div className="space-y-2">
          {watchedValues.items.map((item, index) => (
            <div key={index} className="flex justify-between items-center text-sm">
              <span>{item.description}</span>
              <span>{item.quantity} {t(`invoices.unitOfMeasures.${item.unitOfMeasure}`)} × {item.unitPrice} {CurrencySymbols[watchedValues.currency]} ({TaxRateLabels[item.taxRate]})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    
    {/* Общее сообщение об ошибке валидации для обязательных полей */}
    {(errors.clientId || errors.bankAccountId) && (
      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-600">
          {t('invoices.validation.fillRequiredFields') || 'Пожалуйста, заполните все обязательные поля'}
        </p>
      </div>
    )}
  </div>
)}

{/* Навигационные кнопки */}
<div className="flex items-center justify-between pt-6 border-t border-gray-200">
  <div>
    {currentStep !== 'basic' && (
      <button
        type="button"
        onClick={prevStep}
        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
      >
        ← {t('invoices.multiStep.back')}
      </button>
    )}
  </div>
  
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={handleClose}
      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
    >
      {t('common.cancel')}
    </button>
    
    {currentStep !== 'review' ? (
      <button
        type="button"
        onClick={nextStep}
        disabled={currentStep === 'currency' && !selectedExchangeRate && watchedValues.currency !== Currency.PLN}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t('invoices.multiStep.next')} →
      </button>
    ) : (
      <button
        type="submit"
        disabled={createInvoiceMutation.isPending}
        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
      >
        {createInvoiceMutation.isPending ? t('common.creating') : t('invoices.createInvoice')}
      </button>
    )}
  </div>
</div>
</form>
</div>
</div>
);
};

export default MultiStepInvoiceModal;